/**
 * Notification settings — Step 96.
 *
 * Kai asked for this after the reference's Email Notifications screens, and
 * then: *"I don't want you to match this UI exactly though."* So it keeps what
 * that screen gets right — the Agent / Requester / Cc split, because "what do
 * my agents hear about" is a different sitting from "what do customers hear
 * about" — and drops what it gets wrong.
 *
 * ── One list, not a tab per audience ───────────────────────────────────
 *
 * The reference hides each group behind a tab, so the question people actually
 * arrive with ("why did nobody get told?") takes four clicks and a good memory
 * to answer. Here every group is on one scrolling page under its own heading:
 * the whole of what this desk sends is readable in one pass, which is the only
 * view that makes the question answerable.
 *
 * ── The description is the control's label ─────────────────────────────
 *
 * Each row states what the event is FOR and who receives it. An admin deciding
 * whether to switch something off needs to know who stops hearing about it, and
 * a bare toggle labelled "Status changed" tells them nothing — that is the
 * setting people turn off and then spend a fortnight puzzling over.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';
import { NOTIFICATION_GROUPS, type NotificationGroup } from '@itsm/shared';

interface EventRow {
  key: string;
  group: NotificationGroup;
  label: string;
  description: string;
  audiences: string[];
  defaultOn: boolean;
  locked: boolean;
  enabled: boolean;
  overridden: boolean;
}

/** Roles into words. "assignee, group" is our vocabulary, not an admin's. */
const AUDIENCE_WORDS: Record<string, string> = {
  requester: 'the requester',
  assignee: 'whoever it is assigned to',
  group: 'the group, when nobody owns it',
  watchers: 'anyone watching it',
  mentioned: 'the person tagged',
  approver: 'the approver',
  cc: 'everyone copied in',
};

export function NotificationsPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loaded = useQuery({
    queryKey: qk.notificationSettings,
    queryFn: () => apiFetch<{ events: EventRow[] }>('/api/admin/notifications'),
  });

  const events = loaded.data?.events ?? [];
  // The saved state as a plain map, so dirty is a string compare rather than a
  // per-row diff that has to stay in step with the rows.
  const stored: Record<string, boolean> = Object.fromEntries(
    events.filter((e) => !e.locked).map((e) => [e.key, e.enabled]),
  );
  const current = draft ?? stored;
  const dirty = JSON.stringify(current) !== JSON.stringify(stored);
  useDirty(dirty);

  if (loaded.isError) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm font-medium">Those settings could not be loaded.</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl p-6">
      <h1 className="flex items-center gap-2 text-lg font-semibold">
        <Bell className="h-5 w-5" /> Notifications
      </h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        What this service desk sends, and who receives it. Anything you leave alone follows the product
        default, so an alert added in a later release arrives already set sensibly.
      </p>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {loaded.isPending ? (
        <div className="mt-6 space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        NOTIFICATION_GROUPS.map((group) => {
          const rows = events.filter((e) => e.group === group.key);
          if (!rows.length) return null;
          return (
            <section key={group.key} className="mt-6">
              <h2 className="text-sm font-semibold">{group.label}</h2>
              <p className="mb-2 mt-0.5 text-sm text-muted-foreground">{group.blurb}</p>
              <div className="divide-y rounded-lg border bg-card">
                {rows.map((e) => {
                  const on = e.locked ? true : (current[e.key] ?? e.defaultOn);
                  return (
                    <label
                      key={e.key}
                      className={cn(
                        'flex items-start gap-3 p-3',
                        e.locked ? 'cursor-default opacity-70' : 'cursor-pointer hover:bg-accent/40',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={e.locked}
                        onChange={(ev) => setDraft({ ...current, [e.key]: ev.target.checked })}
                        className="mt-0.5 h-4 w-4 rounded border-input"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                          {e.label}
                          {/* Says WHY it cannot be switched off, rather than
                              leaving a disabled box to be puzzled over. */}
                          {e.locked && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground">
                              Always on — turning it off locks requesters out
                            </span>
                          )}
                          {!e.locked && current[e.key] !== e.defaultOn && (
                            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-normal text-amber-700 dark:text-amber-400">
                              Changed from the default
                            </span>
                          )}
                        </span>
                        <span className="block text-sm text-muted-foreground">{e.description}</span>
                        {/* Who stops hearing about it — the fact an admin
                            actually needs before switching something off. */}
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Goes to {e.audiences.map((a) => AUDIENCE_WORDS[a] ?? a).join(', ')}.
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      <div className="sticky bottom-0 mt-4 flex items-center justify-end gap-3 border-t bg-background/95 py-3 backdrop-blur">
        {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        <Button variant="ghost" disabled={!dirty || saving} onClick={() => setDraft(null)}>
          Discard
        </Button>
        <Button
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true);
            setError(null);
            const res = await apiSend('/api/admin/notifications', 'PUT', { overrides: current });
            setSaving(false);
            if (!res.ok) return setError(res.error ?? 'Those settings could not be saved.');
            setDraft(null);
            void qc.invalidateQueries({ queryKey: qk.notificationSettings });
          }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
